import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import ColumnSelection from "../../../components/CustomComponents/ColumnSelection/ColumnSelection.jsx";
import showErrorToast, { showSuccessToast } from "../../../components/CustomComponents/toast/toast.js";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader.js";
import AddButton from "../../../components/CustomComponents/Buttons/AddButton/AddButton.jsx";
import axiosInstance from "../../../config/axiosApi/axiosApi.jsx";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../../app/itemGridPreferencesSlice.js";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../../utils/preferencesUtils";
import { SortOrderDropDownSelection } from "../../../components/CustomComponents/DropdownSelections/DropDownSelections";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../../staff/dropdownStyles";
import { appSettings } from "../../../app/appSettingsSlice";
import "./../../order/ManageOrderFilters.css";

const ManageDiningAreas = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [originalItemsCount, setOriginalItemsCount] = useState(0);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const lastSelectedIndex = useRef(null);
  const searchTimeoutRef = useRef(null);
  const pageKey = "diningAreas";
  const defaultVisibleColumns = {
    status: true,
    Tables: false,
    Seats: false,
    SortOrder: false,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "sA_Orderby", label: "Order By" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  

  const sortByOptions = [
    { value: "sA_Orderby", label: "Order By" },
    { value: "sA_ID", label: "ID" },
    { value: "sA_Name", label: "Name" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const fetchItemsData = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        "/api/ServiceArea/GetAllServiceAreas",
        {
          params: {
            CompID,
            Search: search?.trim() || "",
          },
        }
      );

      const itemsData = Array.isArray(response.data.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];

      setItems(itemsData);
      if (!search.trim()) {
        setOriginalItemsCount(itemsData.length);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      // Error fetching service areas silently
      showErrorToast("Failed to load service areas");
      setItems([]);
    }
  }, [search, CompID]);

  useEffect(() => {
    if (!CompID) return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchItemsData();
    }, 1000);

    return () => clearTimeout(searchTimeoutRef.current);
  }, [fetchItemsData, CompID]);

  const handleAddItemClick = () => {
    navigate("/diningAreas/addDiningArea");
  };

  const handleSelectItem = (itemId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (items[i]?.id) {
            newSelected.add(items[i].id);
          }
        }
      } else {
        if (newSelected.has(itemId)) {
          newSelected.delete(itemId);
        } else {
          newSelected.add(itemId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(items.map((item) => item.id).filter(Boolean));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No service areas selected for deletion.");
      return;
    }

    if (items.length - selectedItems.size < 1) {
      showErrorToast("At least one service area is required.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected service areas?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.delete("/api/ServiceArea/DeleteServiceArea", {
            params: { ID: itemId, CompID },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      await fetchItemsData();
      showSuccessToast(`${selectedItems.size} service areas deleted successfully.`);
    } catch (err) {
      // Error in bulk delete silently
      showErrorToast("Failed to delete selected service areas.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No service areas selected for editing.");
      return;
    }

    const selectedItemIds = Array.from(selectedItems);
    navigate(`/diningAreas/bulkEdit`, { state: { selectedItemIds } });
  };

  const handleBulkStatusChange = async (publishStatus) => {
    if (selectedItems.size === 0) {
      showErrorToast("No service areas selected for status change.");
      return;
    }

    const previousItems = [...items];
    try {
      setLoading(true);
      setLoading(true);

      const updatedItems = items.map((item) =>
        selectedItems.has(item.id)
          ? { ...item, sA_Status: publishStatus }
          : item
      );
      setItems(updatedItems);

      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.post("/api/ServiceArea/UpdateServiceAreaStatus", {
            CompID,
            ID: itemId,
            SA_Publish: publishStatus,
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      showSuccessToast(
        `${selectedItems.size} service areas status updated successfully.`
      );
    } catch (error) {
      // Error in bulk status change silently
      showErrorToast("Failed to update selected service areas status.");
      setItems(previousItems);
      await fetchItemsData();
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (item) => {
    if (items.length === 1) {
      showErrorToast("At least one service area is required.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${item.sA_Name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const response = await axiosInstance.delete(
        "/api/ServiceArea/DeleteServiceArea",
        {
          params: { ID: item.id, CompID },
        }
      );

      if (response.data.status) {
        setItems((prevItems) => prevItems.filter((i) => i.id !== item.id));
        showSuccessToast("Service area deleted successfully");
      } else {
        showErrorToast(response.data.message || "Deletion failed");
      }
    } catch (err) {
      // Error in handleDeleteItem silently
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(overIndex, 0, draggedItem);

    const updatedItems = newItems.map((item, index) => ({
      ...item,
      sA_SortOrder: index + 1,
    }));

    try {
      setLoading(true);
      const payload = updatedItems.map((item) => ({
        ID: item.id,
        SA_SortOrder: item.sA_SortOrder,
        CompID,
      }));

      const response = await axiosInstance.post(
        "/api/ServiceArea/UpdateServiceAreaSortOrder",
        payload
      );

      if (response.data.status) {
        setItems(updatedItems);
        showSuccessToast("Service areas reordered successfully");
      } else {
        showErrorToast(
          response.data.message || "Failed to update service areas order"
        );
      }
    } catch (error) {
      // Error updating item order silently
      showErrorToast("Failed to update service areas order");
    } finally {
      dragItem.current = null;
      dragOverItem.current = null;
      setLoading(false);
    }
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleEditArea = (item) => {
    navigate(`/diningAreas/updateDiningArea/${item.id}`);
  };

  const toggleItemStatus = async (id, currentStatus) => {
    try {
      const previousItems = [...items];
      const updatedItems = items.map((item) =>
        item.id === id ? { ...item, sA_Status: !currentStatus } : item
      );
      setItems(updatedItems);

      const response = await axiosInstance.post(
        "/api/ServiceArea/UpdateServiceAreaStatus",
        {
          CompID,
          ID: id,
          SA_Publish: !currentStatus,
        }
      );

      if (!response.data.status) {
        showErrorToast(
          response.data.message || "Failed to update service area status"
        );
        setItems(previousItems);
      }
    } catch (error) {
      // Status update failed silently
      showErrorToast("Failed to update service area status");
      setItems(items);
    }
  };

  const renderNoDataMessage = () => {
    if (search.trim() && items.length === 0) {
      return (
        <div className="alert alert-info text-center">
          No Service Area found matching your search criteria.
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Service Area</li>
          </ol>
          <h1 className="page-header mb-0">Manage Service Area</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={handleAddItemClick}
            style={{ 
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Area
          </button>
        </div>
      </div>

      {!loading && !search.trim() && originalItemsCount === 0 && (
        <AddButton onClick={handleAddItemClick} title="Add Area" />
      )}

      {(originalItemsCount > 0 || search.trim()) && (
        <div className="mb-3 mt-2">
          <div className="card border-0">
            <div className="tab-content p-3">
              <div className="tab-pane fade show active" id="allTab">
                {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-4 col-lg-4 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder="Search by area name..."
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                color: '#333'
                              }}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                              options={sortByOptions}
                              styles={dropdownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-xl-3 col-lg-3 col-md-12">
                          <div className="d-flex gap-2">
                            <button
                              className="clear-btn"
                              onClick={clearFilters}
                              title="Clear all filters"
                            >
                              <i className="fa fa-times me-1"></i>
                              Filter
                            </button>

                            <ColumnSelection
                              visibleColumns={visibleColumns}
                              setVisibleColumns={(cols) =>
                                dispatch(setVisibleColumns({ pageKey, columns: cols }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selection Info and Actions (Shown only when areas are selected) */}
                {selectedItems.size > 0 && (
                  <div className="mb-3">
                    <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-primary">
                          {selectedItems.size} area{selectedItems.size > 1 ? 's' : ''} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleDeselectAll}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleBulkEdit}
                          title="Edit Selected Areas"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleBulkStatusChange(true)}
                          title="Publish Selected Areas"
                        >
                          <i className="fa fa-check-circle me-1"></i>
                          Publish
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleBulkStatusChange(false)}
                          title="Unpublish Selected Areas"
                        >
                          <i className="fa fa-times-circle me-1"></i>
                          Unpublish
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                          title="Delete Selected Areas"
                        >
                          <i className="fa fa-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {renderNoDataMessage()}
                {items.length > 0 && (
                  <div
                    className="table-responsive mt-3"
                    style={{ minHeight: "200px" }}
                  >
                    <table className="table table-hover">
                      <thead className="bg-light">
                        <tr>
                          <th className="pt-2 pb-2">
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                items.length > 0 &&
                                selectedItems.size === items.length
                              }
                            />
                          </th>
                          <th className="pt-2 pb-2">Area Name</th>
                          {visibleColumns.Tables && (
                            <th className="pt-2 pb-2 text-center">Tables</th>
                          )}
                          {visibleColumns.Seats && (
                            <th className="pt-2 pb-2 text-center">Seats</th>
                          )}
                          {visibleColumns.SortOrder && (
                            <th className="pt-2 pb-2 text-center">Sort Order</th>
                          )}
                          {visibleColumns.status && (
                            <th className="pt-2 pb-2 text-center">Status</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr
                            key={item.id || index}
                            draggable={false}
                            onDragStart={(e) => {
                              // Only allow dragging from the grab icon
                              if (!e.target.classList.contains('fa-bars')) {
                                e.preventDefault();
                              }
                            }}
                            onDragEnter={(e) => handleDragEnter(e, index)}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                          >
                            <td className="align-middle">
                              <input
                                type="checkbox"
                                checked={selectedItems.has(item.id)}
                                onChange={(e) =>
                                  handleSelectItem(item.id, index, e)
                                }
                                disabled={!item.id}
                                data-chkbox-shiftsel="type1"
                              />
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => item.id && handleEditArea(item)}
                              style={{ cursor: item.id ? "pointer" : "default" }}
                            >
                              {item.sA_Name || "N/A"}
                            </td>
                            {visibleColumns.Tables && (
                              <td
                                className="align-middle text-center"
                                onClick={() => item.id && handleEditArea(item)}
                                style={{ cursor: item.id ? "pointer" : "default" }}
                              >
                                {item.numberofTables || "N/A"}
                              </td>
                            )}
                            {visibleColumns.Seats && (
                              <td
                                className="align-middle text-center"
                                onClick={() => item.id && handleEditArea(item)}
                                style={{ cursor: item.id ? "pointer" : "default" }}
                              >
                                {item.numberofSeats || "N/A"}
                              </td>
                            )}
                            {visibleColumns.SortOrder && (
                              <td
                                className="align-middle text-center"
                                onClick={() => item.id && handleEditArea(item)}
                                style={{ cursor: item.id ? "pointer" : "default" }}
                              >
                                {item.sA_SortOrder || "N/A"}
                              </td>
                            )}
                            {visibleColumns.status && (
                              <td className="align-middle text-center">
                                <div
                                  className={`toggle-switch ${item.sA_Status ? "active" : ""
                                    }`}
                                >
                                  <label className="switch">
                                    <input
                                      type="checkbox"
                                      checked={!!item.sA_Status}
                                      onChange={() =>
                                        item.id &&
                                        toggleItemStatus(item.id, item.sA_Status)
                                      }
                                      disabled={!item.id}
                                    />
                                    <span 
                                      className="slider round"
                                      style={{
                                        backgroundColor: item.sA_Status ? themeColor : '#ccc'
                                      }}
                                    ></span>
                                  </label>
                                </div>
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <div className="d-flex justify-content-center gap-2">
                                {/* Drag Handle */}
                                <i
                                  className="fas fa-bars text-secondary"
                                  style={{
                                    cursor: "grab",
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #6c757d',
                                    backgroundColor: 'transparent',
                                    transition: 'all 0.2s ease',
                                    opacity: !item.id ? 0.5 : 1
                                  }}
                                  draggable={!!item.id}
                                  onDragStart={item.id ? (e) => handleDragStart(e, index) : undefined}
                                  onDragEnd={handleDragEnd}
                                  title="Drag to reorder"
                                  onMouseEnter={(e) => {
                                    if (item.id) {
                                      e.currentTarget.style.backgroundColor = '#6c757d';
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (item.id) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = '#6c757d';
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }
                                  }}
                                ></i>

                                {/* Edit Icon */}
                                <button
                                  onClick={() => handleEditArea(item)}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Edit Service Area"
                                  disabled={!item.id}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    transition: 'all 0.2s ease',
                                    opacity: !item.id ? 0.5 : 1
                                  }}
                                  onMouseEnter={(e) => {
                                    if (item.id) {
                                      e.currentTarget.style.backgroundColor = themeColor;
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (item.id) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = themeColor;
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                </button>

                                {/* Delete Icon */}
                                <button
                                  onClick={() => handleDeleteItem(item)}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete Service Area"
                                  disabled={!item.id}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #dc3545',
                                    color: '#dc3545',
                                    transition: 'all 0.2s ease',
                                    opacity: !item.id ? 0.5 : 1
                                  }}
                                  onMouseEnter={(e) => {
                                    if (item.id) {
                                      e.currentTarget.style.backgroundColor = '#dc3545';
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (item.id) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = '#dc3545';
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDiningAreas;